import { useState, useId } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api.js';
import { t, getal } from '../i18n.js';
import { landenGesorteerd, landnaam, landMidden, plaatsenVan, plaatsBij } from '../landen.js';
import { Link } from 'react-router-dom';
import IpReeksen from '../components/IpReeksen.jsx';
import KlantModules from '../components/KlantModules.jsx';
import KlantDiensten from '../components/KlantDiensten.jsx';
import KlantDomeinen from '../components/KlantDomeinen.jsx';
import KlantMeldingen from './KlantMeldingen.jsx';
import BeheerSensoren from './BeheerSensoren.jsx';

/**
 * Beheer › Klanten, als lijst met detail: links de klanten met hun kerncijfers, rechts de gekozen klant met de tabs
 * Gegevens, Modules en IP-reeksen (en Meekijken). Een klant aanmaken gebeurt ook rechts.
 *
 * Een klant aanmaken en zijn naam, plaats op de kaart of account in het klantportaal wijzigen: zie
 * docs/besluiten/klanten-aanmaken.md en klanten-via-het-portaal.md (alleen gebruikers van dat account komen binnen). De code ligt vast zodra hij er is: die staat in links en in de
 * adressen van SOC-schermen. Wie welke klanten ziet, staat onder Medewerkers.
 */
/* De plaats van een locatie in woorden: land en plaats, of een eigen plek, of niets. */
const plaatsTekst = (loc) => {
  if (loc.lat === null || loc.lat === undefined) return t('Geen plaats op de kaart');
  const b = plaatsBij(loc.lat, loc.lon);
  if (!b) return t('Eigen plek');
  const land = landnaam(landenGesorteerd().find((l) => l.code === b.land) ?? {});
  return b.plaats ? `${b.plaats}, ${land}` : land;
};

/**
 * Twee tabelcellen: eerst het land kiezen, daarna de plaats als zoeklijst (typen om te filteren). Daaruit volgt de plek
 * op de kaart (lat/lon); een land zonder plaats gebruikt het midden van het land, "geen" haalt de plek weg. Niemand
 * typt coördinaten.
 */
function PlaatsCellen({ land, plaats, zet }) {
  const lijstId = useId();
  const plaatsen = land ? plaatsenVan(land) : [];
  const kiesPlaats = (naam) => {
    const p = plaatsen.find((x) => x.name.toLowerCase() === naam.trim().toLowerCase());
    const w = p ?? landMidden(land);
    zet({ land, plaats: p ? p.name : naam, lat: w?.lat ?? null, lon: w?.lon ?? null });
  };
  return (
    <>
      <td>
        <select className="input" value={land} aria-label={t('Land')}
          onChange={(e) => { const code = e.target.value; const m = landMidden(code); zet({ land: code, plaats: '', lat: m?.lat ?? null, lon: m?.lon ?? null }); }}>
          <option value="">{t('— Geen plaats —')}</option>
          {landenGesorteerd().map((l) => <option key={l.code} value={l.code}>{landnaam(l)}</option>)}
        </select>
      </td>
      <td>
        <input className="input" list={lijstId} value={plaats} disabled={!land} aria-label={t('Plaats')}
          placeholder={land ? t('Zoek een plaats…') : t('Kies eerst een land')}
          onChange={(e) => kiesPlaats(e.target.value)} />
        <datalist id={lijstId}>{plaatsen.map((p) => <option key={p.name} value={p.name} />)}</datalist>
      </td>
    </>
  );
}

export default function BeheerKlanten({ klanten, doe }) {
  const [gekozen, zetGekozen] = useState(klanten[0]?.code ?? null);
  const [tab, zetTab] = useState('gegevens');
  const [zoek, zetZoek] = useState('');
  const mods = useQuery({ queryKey: ['modules-overzicht'], queryFn: api.modulesOverzicht });
  const aan = new Map((mods.data?.data ?? []).map((x) => [x.tenant.code, x.modules.filter((m) => m.state === 'aan' && m.id !== 'basis').length]));
  const k = klanten.find((x) => x.code === gekozen);
  const lijst = klanten.filter((x) => !zoek.trim() || `${x.name} ${x.code}`.toLowerCase().includes(zoek.trim().toLowerCase()));
  const kies = (code) => { zetGekozen(code); zetTab('gegevens'); };

  return (
    <div className="klantbeheer">
      <section className="card klantlijst">
        <div className="klantlijst-kop">
          <input className="input" value={zoek} onChange={(e) => zetZoek(e.target.value)} placeholder={t('Zoek een klant')} aria-label={t('Zoek een klant')} />
          <button type="button" className="btn primary" onClick={() => kies('+')}>+ {t('Klant')}</button>
        </div>
        <div className="klantlijst-rijen">
          {lijst.map((x) => (
            <button key={x.code} type="button" className={`klantrij ${x.code === gekozen ? 'on' : ''}`} onClick={() => kies(x.code)}>
              <span className="klantrij-naam"><b>{x.name}</b>{x.own && <span className="pill">{t('eigen')}</span>}{!x.active && <span className="pill wacht">{t('uit')}</span>}{x.trialUntil && <span className="pill wacht">{t('proef')}</span>}</span>
              <span className="klantrij-cijfers">
                <span title={t('Bronnen')}>{t('{n} bronnen', { n: getal(x.sources) })}</span>
                <span title={t('Modules aan')}>{t('{n} modules', { n: getal(aan.get(x.code) ?? 0) })}</span>
                <span title={t('Klantgebruikers')}>{t('{n} gebruikers', { n: getal(x.customerUsers ?? 0) })}</span>
              </span>
            </button>
          ))}
          {!lijst.length && <p className="empty">{t('Geen klant gevonden.')}</p>}
        </div>
      </section>

      <section className="card klantdetail">
        {gekozen === '+' ? <Aanmaken doe={doe} onKlaar={(code) => kies(code)} />
          : !k ? <p className="empty">{t('Kies links een klant.')}</p> : (
            <>
              <header className="klantdetail-kop">
                <div><h2 style={{ margin: 0 }}>{k.name}</h2>
                  <span className="muted mono" style={{ fontSize: 12.5 }}>{k.code}</span></div>
                <Link className="btn" to={`/meekijken/${k.code}`}>{t('Meekijken als klant')}</Link>
              </header>
              <dl className="klantcijfers">
                <div><dt>{t('Bronnen')}</dt><dd>{getal(k.sources)}</dd></div>
                <div><dt>{t('Medewerkers')}</dt><dd>{getal(k.employees)}</dd></div>
                <div><dt>{t('Klantgebruikers')}</dt><dd>{getal(k.customerUsers ?? 0)}</dd></div>
                <div><dt>{t('Modules aan')}</dt><dd>{getal(aan.get(k.code) ?? 0)}</dd></div>
              </dl>
              <div className="filters" style={{ margin: '14px 0' }}>
                {[['gegevens', t('Gegevens')], ['locaties', t('Locaties')], ['modules', t('Modules')], ['meldingen', t('Meldingen')], ['sensoren', t('Sensoren')], ['diensten', t('Diensten en NIS2')], ['domeinen', t('Maildomein')], ['reeksen', t('IP-reeksen')], ...(k.own ? [] : [['verwijderen', t('Verwijderen')]])].map(([id, l]) => (
                  <button key={id} type="button" className="chip" aria-pressed={tab === id} onClick={() => zetTab(id)}>{l}</button>))}
              </div>
              {tab === 'gegevens' && <Gegevens key={k.code} k={k} doe={doe} />}
              {tab === 'locaties' && <KlantLocaties key={k.code} k={k} doe={doe} />}
              {tab === 'modules' && <KlantModules key={k.code} klant={k} />}
              {tab === 'meldingen' && <KlantMeldingen key={k.code} code={k.code} />}
              {tab === 'sensoren' && <BeheerSensoren key={k.code} klanten={klanten} alleenKlant={k.id} />}
              {tab === 'diensten' && <KlantDiensten key={k.code} klant={k} />}
              {tab === 'domeinen' && <KlantDomeinen key={k.code} klant={k} />}
              {tab === 'reeksen' && <IpReeksen key={k.code} klant={k} />}
              {tab === 'verwijderen' && <KlantVerwijderen key={k.code} k={k} doe={doe} />}
            </>
          )}
      </section>
    </div>
  );
}

/** De gegevens van een klant: naam en account in het klantportaal. De code ligt vast. De plaats(en) op de kaart staan onder Locaties. */
function Gegevens({ k, doe }) {
  const [v, zetV] = useState({ name: k.name, portaal: k.portalAccount ?? '' });
  const [melding, zetMelding] = useState(null);
  const zet = (veld) => (e) => { const w = e.target.value; zetV((x) => ({ ...x, [veld]: w })); };
  const bewaar = (e) => {
    e.preventDefault();
    zetMelding(null);
    doe(async () => {
      try {
        await api.wijzigKlant(k.code, { name: v.name.trim(), portalAccount: v.portaal.trim() });
        zetMelding({ goed: true, tekst: t('Opgeslagen.') });
      } catch (err) { zetMelding({ goed: false, tekst: t(err.details ? Object.values(err.details)[0] : err.message) }); }
    });
  };
  return (
    <form className="form" onSubmit={bewaar}>
      {melding && <div className={`melder ${melding.goed ? 'blauw' : 'rood'}`}>{melding.tekst}</div>}
      <div className="two">
        <label className="field"><span>{t('Naam')}</span><input className="input" value={v.name} onChange={zet('name')} /></label>
        <label className="field"><span>{t('Code')}</span><input className="input mono" value={k.code} disabled />
          <small>{t('Staat vast: in links en in de adressen van SOC-schermen.')}</small></label>
      </div>
      <label className="field"><span>{t('Account in het klantportaal (niet verplicht)')}</span>
        <input className="input mono" value={v.portaal} onChange={zet('portaal')} placeholder="0015g00000AbCdEFGH" maxLength={18} autoComplete="off" />
        <small>{k.portalAccount ? t('{n} klantgebruikers via dit account.', { n: getal(k.customerUsers ?? 0) }) : t('Het AccountId uit Salesforce (18 tekens). Alleen gebruikers van dit account komen via het klantportaal bij deze klant binnen.')}</small></label>
      <div className="actions"><button className="btn primary" disabled={v.name.trim().length < 2}>{t('Opslaan')}</button>
        {!k.own && <button type="button" className={k.active ? 'btn' : 'btn primary'} onClick={() => doe(async () => {
          try { await api.zetKlantActief(k.code, !k.active); zetMelding({ goed: true, tekst: k.active ? t('Gedeactiveerd: verborgen, verzamelt niets nieuws.') : t('Weer geactiveerd.') }); }
          catch (err) { zetMelding({ goed: false, tekst: t(err.details ? Object.values(err.details)[0] : err.message) }); }
        })}>{k.active ? t('Deactiveren') : t('Activeren')}</button>}
      </div>
      {!k.own && k.active && <p className="noot">{t('Deactiveren verbergt de klant en stopt het verzamelen; de gegevens blijven staan en het is omkeerbaar. Persoonsgegevens echt wissen doe je apart, onder Verwijderen.')}</p>}
    </form>
  );
}

/**
 * De locaties (vestigingen) van een klant in een tabel: naam, land en plaats per rij. Met + voeg je een lege rij toe
 * om in te vullen. De hardware koppel je per bron aan een locatie.
 */
function KlantLocaties({ k, doe }) {
  const [nieuwe, zetNieuwe] = useState([]);
  const voegRijToe = () => zetNieuwe((r) => [...r, { sleutel: `n${Date.now()}${r.length}` }]);
  const sluitRij = (sleutel) => zetNieuwe((r) => r.filter((x) => x.sleutel !== sleutel));
  const leeg = !k.locations.length && !nieuwe.length;
  return (
    <div className="stack">
      <p className="muted">{t('Een klant kan meer vestigingen hebben, elk met eigen hardware (firewall, ISE, ESA). Op de kaart krijgt elke locatie een eigen punt; de boog van een aanval gaat naar de locatie van de bron die het logde. Koppel een bron aan een locatie bij de bron zelf.')}</p>
      <div style={{ overflowX: 'auto' }}>
        <table className="loctabel">
          <thead><tr><th>{t('Naam van de locatie')}</th><th>{t('Land')}</th><th>{t('Plaats')}</th><th aria-label={t('Acties')}></th></tr></thead>
          <tbody>
            {k.locations.map((l) => <LocatieRij key={l.id} code={k.code} loc={l} doe={doe} />)}
            {nieuwe.map((r) => <NieuweRij key={r.sleutel} code={k.code} doe={doe} opSluit={() => sluitRij(r.sleutel)} />)}
            {leeg && <tr><td colSpan={4} className="leeg">{t('Nog geen locaties. Klik op + om de eerste vestiging toe te voegen.')}</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="actions"><button type="button" className="btn primary" onClick={voegRijToe}>+ {t('Locatie')}</button></div>
    </div>
  );
}

/** Eén locatie als tabelrij: naam en plaats wijzigen, of weghalen (de bronnen vallen dan terug op de eerste locatie). */
function LocatieRij({ code, loc, doe }) {
  const begin = plaatsBij(loc.lat, loc.lon);
  const [v, zetV] = useState({ name: loc.name, land: begin?.land ?? '', plaats: begin?.plaats ?? '', lat: loc.lat, lon: loc.lon });
  const [weg, zetWeg] = useState(false);
  const gewijzigd = v.name !== loc.name || v.lat !== loc.lat || v.lon !== loc.lon;
  return (
    <tr>
      <td>
        <input className="input" value={v.name} aria-label={t('Naam van de locatie')} onChange={(e) => zetV((x) => ({ ...x, name: e.target.value }))} />
        {!begin && loc.lat != null && <small className="muted">{plaatsTekst(loc)}</small>}
      </td>
      <PlaatsCellen land={v.land} plaats={v.plaats} zet={(p) => zetV((x) => ({ ...x, ...p }))} />
      <td><div className="loc-acties">
        <button type="button" className="btn primary" disabled={!gewijzigd || !v.name.trim()} onClick={() => doe(() => api.wijzigLocatie(code, loc.id, { name: v.name.trim(), lat: v.lat, lon: v.lon }))}>{t('Opslaan')}</button>
        {weg
          ? <button type="button" className="btn danger" onClick={() => doe(() => api.verwijderLocatie(code, loc.id))}>{t('Zeker weten?')}</button>
          : <button type="button" className="btn" onClick={() => zetWeg(true)}>{t('Weghalen')}</button>}
      </div></td>
    </tr>
  );
}

/** Een nieuwe, nog niet opgeslagen locatie-rij (via +). Opslaan maakt hem aan; de lijst ververst dan vanzelf. */
function NieuweRij({ code, doe, opSluit }) {
  const [v, zetV] = useState({ name: '', land: '', plaats: '', lat: null, lon: null });
  return (
    <tr>
      <td><input className="input" autoFocus value={v.name} placeholder={t('Hoofdlocatie')} aria-label={t('Naam van de locatie')} onChange={(e) => zetV((x) => ({ ...x, name: e.target.value }))} /></td>
      <PlaatsCellen land={v.land} plaats={v.plaats} zet={(p) => zetV((x) => ({ ...x, ...p }))} />
      <td><div className="loc-acties">
        <button type="button" className="btn primary" disabled={!v.name.trim()} onClick={() => doe(async () => { await api.maakLocatie(code, { name: v.name.trim(), lat: v.lat, lon: v.lon }); opSluit(); })}>{t('Toevoegen')}</button>
        <button type="button" className="btn" onClick={opSluit}>{t('Annuleren')}</button>
      </div></td>
    </tr>
  );
}

function Aanmaken({ doe, onKlaar }) {
  const [nieuw, zetNieuw] = useState({ code: '', name: '', portaal: '', proef: false, domein: '' });
  const [fout, zetFout] = useState(null);
  const maak = (e) => {
    e.preventDefault();
    zetFout(null);
    doe(async () => {
      try {
        await api.maakKlant({ code: nieuw.code.trim(), name: nieuw.name.trim(), portalAccount: nieuw.portaal.trim(), trial: nieuw.proef, domain: nieuw.domein.trim() });
        onKlaar(nieuw.code.trim());
      } catch (err) { zetFout(t(err.details ? Object.values(err.details)[0] : err.message)); }
    });
  };
  return (
    <>
      <header><h2>{t('Klant aanmaken')}</h2></header>
      <form className="form" onSubmit={maak}>
        {fout && <div className="melder rood">{fout}</div>}
        <div className="two">
          <label className="field"><span>{t('Naam')}</span>
            <input className="input" value={nieuw.name} onChange={(e) => zetNieuw((x) => ({ ...x, name: e.target.value }))} placeholder="Noordkade BV" /></label>
          <label className="field"><span>{t('Code')}</span>
            <input className="input mono" value={nieuw.code} onChange={(e) => zetNieuw((x) => ({ ...x, code: e.target.value.toLowerCase() }))} placeholder="noordkade" autoComplete="off" />
            <small>{t('Kleine letters, cijfers en streepjes. Staat daarna vast: in links en in de adressen van SOC-schermen.')}</small></label>
        </div>
        <label className="field"><span>{t('Maildomein (niet verplicht)')}</span>
          <input className="input mono" value={nieuw.domein} onChange={(e) => zetNieuw((x) => ({ ...x, domein: e.target.value }))} placeholder="noordkade.nl" autoComplete="off" />
          <small>{t('Het deel achter de @. ADTS ZERO controleert meteen of mail en website op orde zijn, zonder te scannen.')}</small></label>
        <label className="vink"><input type="checkbox" checked={nieuw.proef} onChange={(e) => zetNieuw((x) => ({ ...x, proef: e.target.checked }))} />
          {' '}{t('Proefklant (30 dagen): nog geen modules, telt niet mee in het gemiddelde')}</label>
        <label className="field"><span>{t('Account in het klantportaal (niet verplicht)')}</span>
          <input className="input mono" value={nieuw.portaal} onChange={(e) => zetNieuw((x) => ({ ...x, portaal: e.target.value }))} placeholder="0015g00000AbCdEFGH" maxLength={18} autoComplete="off" />
          <small>{t('Het AccountId uit Salesforce (18 tekens). Alleen gebruikers van dit account komen via het klantportaal bij deze klant binnen.')}</small></label>
        <p className="noot">{t('De locaties (vestigingen) met hun plaats op de kaart stel je in na het aanmaken, onder Locaties.')}</p>
        <div className="actions"><button className="btn primary" disabled={nieuw.name.trim().length < 2 || !/^[a-z0-9-]{2,40}$/.test(nieuw.code.trim())}>{t('Klant aanmaken')}</button></div>
        <p className="noot">{t('Jij krijgt meteen toegang tot de nieuwe klant. Anderen zet je erbij onder Medewerkers.')}</p>
      </form>
    </>
  );
}

/** Beheer › Klanten › Verwijderen (AVG): persoonsgegevens en data wissen, onomkeerbaar, met de naam ter bevestiging. */
function KlantVerwijderen({ k, doe }) {
  const [naam, zetNaam] = useState('');
  const [melding, zetMelding] = useState(null);
  const klopt = naam.trim() === k.name;
  return (
    <div>
      {melding && <div className={`melder ${melding.goed ? 'blauw' : 'rood'}`} style={{ marginBottom: 10 }}>{melding.tekst}</div>}
      <div className="callout warn">
        <b>{t('Persoonsgegevens wissen is onomkeerbaar.')}</b>
        <p>{t('Alle data van deze klant wordt gewist: syslog, sessies, alerts, Secure Connect, mail, endpoints, DNS, scans, blokkades en de inrichting. De klantgebruikers worden geanonimiseerd. De klant blijft als lege, gedeactiveerde schil bestaan, en het logboek blijft (dat moet voor de verantwoording).')}</p>
        <p>{t('Wil je de klant alleen tijdelijk verbergen? Gebruik dan Deactiveren onder Gegevens.')}</p>
      </div>
      <label className="field" style={{ maxWidth: 380, marginTop: 14 }}><span>{t('Typ ter bevestiging de naam over: {naam}', { naam: k.name })}</span>
        <input className="input" value={naam} onChange={(e) => zetNaam(e.target.value)} autoComplete="off" placeholder={k.name} /></label>
      <div className="actions"><button type="button" className="btn danger" disabled={!klopt} onClick={() => doe(async () => {
        try { const r = await api.wisKlant(k.code, naam.trim()); zetMelding({ goed: true, tekst: t('Gewist: {n} rijen verwijderd, {k} klantgebruikers geanonimiseerd.', { n: getal(r.data.verwijderd), k: getal(r.data.klantgebruikers) }) }); zetNaam(''); }
        catch (err) { zetMelding({ goed: false, tekst: t(err.details ? Object.values(err.details)[0] : err.message) }); }
      })}>{t('Persoonsgegevens wissen')}</button></div>
    </div>
  );
}
